"use client";

import { type MouseEvent, type ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { PrivateEntertainmentLayoutHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";

const BG = "#0a0a0a";
const TEXT = "#f4f5f7";
const MUTED = "rgba(244,245,247,0.72)";
const LINE = "rgba(255,255,255,0.14)";
const ACCENT = "#FF5147";

const PHOTOS = {
  hero: "https://storage.googleapis.com/cuo-website/Crowd%20shot%20esb.jpg",
  katz: "/images/Katz_s_Storefront.jpg",
  esbRoom: "https://storage.googleapis.com/cuo-website/Socializing%20at%20ESB.jpg",
  comedy: "https://storage.googleapis.com/cuo-website/Sam%20Morril%20ESB.jpg",
  stageMagic: "https://storage.googleapis.com/cuo-website/Title%20Card%20Magic%20Show.jpg",
  closeUp: "https://storage.googleapis.com/cuo-website/Fette%20Sau%20(RIP).jpg",
  strollStrip: "https://storage.googleapis.com/cuo-website/Burton%20Magic%20Show.jpg",
  interactive:
    "https://storage.googleapis.com/cuo-website/Astor%20Place%20Magic%20Show.jpg",
  production: "https://storage.googleapis.com/cuo-website/Gabby%20Bryan%20Furnish.jpg",
  sanVicente: "https://storage.googleapis.com/cuo-website/San%20Vicente.avif",
  food: "https://storage.googleapis.com/cuo-website/Food%20at%20Veselka%202%20(small).jpg",
};

const FORMATS = [
  {
    title: "Private Comedy Show",
    line: "Turn your space into a comedy club for the night.",
    body: "Professional stand-up comedians, a host, and a fully produced live show. Great for private parties, tenant events, client entertainment, and hospitality venues.",
    facts: [
      ["Best for", "30+ guests"],
      ["Format", "Seated stage show"],
      ["Runtime", "Approximately 45–75 minutes, customizable"],
    ],
    image: PHOTOS.comedy,
    alt: "Sam Morril performing at a ComedyUO show in the Empire State Building",
  },
  {
    title: "Private Stage Magic Show",
    line: "An entire room, captivated.",
    body: "An interactive magic performance where everyone shares the same experience. Perfect for intimate private gatherings that want a centerpiece moment.",
    facts: [
      ["Best for", "Approximately 15–50 guests"],
      ["Format", "Seated, audience-facing show"],
      ["Runtime", "Approximately 30–60 minutes"],
    ],
    image: PHOTOS.stageMagic,
    alt: "ComedyUO stage magic title card from a live show",
  },
  {
    title: "Strolling & Close-up Magic",
    line: "The entertainment comes to your guests.",
    body: "Magicians move through the space, performing remarkable magic for small groups. Guests can keep drinking, talking, and mingling. No stage or formal seating required.",
    facts: [
      ["Best for", "Cocktail receptions, networking events, restaurant gatherings"],
      ["Format", "Roving small-group performances"],
      ["Runtime", "Approximately 60–120 minutes"],
    ],
    image: PHOTOS.closeUp,
    alt: "Strolling magic at a ComedyUO show at Fette Sau",
  },
  {
    title: "Magic for Kids & Families",
    line: "Big reactions from the littlest guests.",
    body: "Playful, age-appropriate, highly interactive magic for children's celebrations, community programming, and family-friendly events.",
    facts: [
      ["Best for", "Kids' parties and family events"],
      ["Format", "Interactive seated or close-up magic"],
      ["Runtime", "Approximately 30–60 minutes"],
    ],
    image: PHOTOS.interactive,
    alt: "Interactive ComedyUO magic with the audience up close",
  },
] as const;

const GLANCE = [
  ["Private Comedy", "30+ guests", "Seated live show", "$3,000–$6,000"],
  ["Private Stage Magic", "Intimate to large gatherings", "Interactive performance", "$3,000–$6,000"],
  ["Strolling Magic", "Cocktail events", "No stage required", "$3,000–$6,000"],
  ["Kids & Family Magic", "Family programming", "Interactive entertainment", "$3,000–$6,000"],
  ["A/V only", "Sound and setup", "No talent required", "$1,500"],
];

const STRIP = [
  { src: PHOTOS.katz, alt: "Katz's Deli, a ComedyUO venue" },
  { src: PHOTOS.esbRoom, alt: "Guests at a ComedyUO night in the Empire State Building" },
  { src: PHOTOS.strollStrip, alt: "ComedyUO magic at Burton" },
  { src: PHOTOS.food, alt: "Food at a ComedyUO show at Veselka" },
];

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
}

export default function PrivateEntertainmentPage() {
  return (
    <Box sx={{ backgroundColor: BG, color: TEXT, minHeight: "100vh", overflowX: "hidden" }}>
      <PrivateEntertainmentLayoutHelmet />

      <Box
        component="section"
        aria-labelledby="portal-hero-title"
        sx={{
          position: "relative",
          minHeight: { xs: "88vh", md: "92vh" },
          display: "flex",
          alignItems: "flex-end",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={PHOTOS.hero}
          alt="A full room at a ComedyUO show in the Empire State Building"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center 30%",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: {
              xs: "linear-gradient(180deg, rgba(10,10,10,0.62) 0%, rgba(10,10,10,0.88) 100%)",
              md: "linear-gradient(90deg, rgba(10,10,10,0.96) 0%, rgba(10,10,10,0.9) 30%, rgba(10,10,10,0.55) 55%, rgba(10,10,10,0.28) 100%), linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.2) 42%, rgba(10,10,10,0.62) 100%)",
            },
          }}
        />
        <Box sx={{ position: "relative", width: "100%", maxWidth: 1120, mx: "auto", px: { xs: 2.5, md: 4 }, pb: { xs: 5, md: 9 }, pt: { xs: 8, md: 12 } }}>
          <Typography sx={{ fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: ACCENT, mb: 2 }}>
            Prepared for Kings Athletic Club
          </Typography>
          <Typography id="portal-hero-title" component="h1" sx={{ color: TEXT, fontWeight: 600, letterSpacing: "-0.03em", fontSize: { xs: "2.35rem", sm: "2.7rem", md: "4.6rem" }, lineHeight: 1.02, maxWidth: 820, mb: 2.5 }}>
            Your Space.
            <br />
            Unforgettable Nights.
          </Typography>
          <Typography sx={{ fontSize: { xs: "1.05rem", md: "1.25rem" }, color: "#fff", maxWidth: 560, lineHeight: 1.45, mb: 2 }}>
            Private comedy and magic experiences, brought to life in your venues.
          </Typography>
          <Typography sx={{ maxWidth: 520, color: "rgba(255,255,255,0.92)", lineHeight: 1.55, mb: 4 }}>
            From intimate cocktail parties to full-room comedy shows, ComedyUO brings the talent, production, and energy. You bring the space.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <Box
              component="a"
              href="#formats"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                scrollToId("formats");
              }}
              sx={ghostBtn}
            >
              Explore the Formats
            </Box>
          </Box>
        </Box>
      </Box>

      <Section id="who" labelledBy="who-title">
        <Eyebrow>Who we are</Eyebrow>
        <Typography id="who-title" component="h2" sx={h2}>
          We turn unexpected spaces into unforgettable shows.
        </Typography>
        <Typography sx={{ ...body, maxWidth: 680, mb: 4 }}>
          ComedyUO is a New York City entertainment company producing comedy and magic in places you wouldn&apos;t expect. From Katz&apos;s Deli to the Empire State Building, we&apos;ve produced 350+ shows with more than 250 comedians.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "1.4fr 1fr 1fr 1fr" },
            gap: 1,
          }}
        >
          {STRIP.map((shot) => (
            <Box
              key={shot.src}
              component="img"
              src={shot.src}
              alt={shot.alt}
              loading="lazy"
              sx={{
                width: "100%",
                height: { xs: 132, sm: 180, md: 220 },
                objectFit: "cover",
                display: "block",
              }}
            />
          ))}
        </Box>
        <Box aria-hidden="true" sx={{ display: { xs: "none", md: "block" }, height: 78 }}>
          <Box
            component="svg"
            viewBox="0 0 1000 78"
            preserveAspectRatio="none"
            sx={{
              width: "100%",
              height: "100%",
              display: "block",
              "& path": {
                fill: "none",
                stroke: ACCENT,
                strokeWidth: 1.5,
                vectorEffect: "non-scaling-stroke",
                strokeDasharray: "7 8",
                animation: "plugFlow 0.9s linear infinite",
              },
              "@keyframes plugFlow": {
                to: { strokeDashoffset: -30 },
              },
              "@media (prefers-reduced-motion: reduce)": {
                "& path": { animation: "none" },
              },
            }}
          >
            <path d="M159 0 V30 H500 V70" />
            <path d="M432 0 V30" />
            <path d="M659 0 V30" />
            <path d="M886 0 V30 H500" />
          </Box>
        </Box>
        <Box
          aria-hidden="true"
          sx={{
            display: { xs: "block", md: "none" },
            width: "1.5px",
            height: 40,
            mx: "auto",
            backgroundImage: `repeating-linear-gradient(${ACCENT} 0 7px, transparent 7px 14px)`,
            backgroundSize: "100% 28px",
            animation: "plugFlowMobile 0.85s linear infinite",
            "@keyframes plugFlowMobile": {
              to: { backgroundPosition: "0 28px" },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
        <Box
          sx={{
            maxWidth: 460,
            mx: "auto",
            border: `1px solid ${LINE}`,
            borderTop: `2px solid ${ACCENT}`,
            background: "#121214",
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: ACCENT, mb: 1.25 }}>
            For Kings Athletic Club
          </Typography>
          <Typography id="rooms-title" component="h3" sx={{ color: TEXT, fontWeight: 560, letterSpacing: "-0.03em", fontSize: { xs: "1.45rem", sm: "1.7rem", md: "2.15rem" }, lineHeight: 1.15 }}>
            Plug us into Kings Athletic Club.
          </Typography>
        </Box>
      </Section>

      <Section id="formats" labelledBy="formats-title">
        <Eyebrow>Find your format</Eyebrow>
        <Typography id="formats-title" component="h2" sx={h2}>
          The right show for the right room.
        </Typography>
        <Typography sx={{ ...body, maxWidth: 640, mb: 4 }}>
          Whether it&apos;s 20 guests around a table or 100 people ready for a show, we&apos;ll tailor the entertainment to the space.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 3, md: 2.5 } }}>
          {FORMATS.map((format) => (
            <Box key={format.title} sx={{ border: `1px solid ${LINE}`, background: "#111113" }}>
              <Box
                component="img"
                src={format.image}
                alt={format.alt}
                loading="lazy"
                sx={{ width: "100%", height: { xs: 220, md: 260 }, objectFit: "cover", display: "block" }}
              />
              <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography component="h3" sx={{ color: TEXT, fontSize: "0.78rem", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 650, mb: 1.25 }}>
                  {format.title}
                </Typography>
                <Typography sx={{ color: TEXT, fontSize: "1.2rem", fontWeight: 550, letterSpacing: "-0.02em", mb: 1.25 }}>
                  {format.line}
                </Typography>
                <Typography sx={{ ...body, mb: 2.5 }}>{format.body}</Typography>
                <Box component="dl" sx={{ m: 0 }}>
                  {format.facts.map(([label, value]) => (
                    <Box key={label} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "88px 1fr" }, gap: { xs: 0.35, sm: 1.5 }, py: 1, borderTop: `1px solid ${LINE}` }}>
                      <Typography component="dt" sx={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: MUTED, m: 0 }}>
                        {label}
                      </Typography>
                      <Typography component="dd" sx={{ color: TEXT, m: 0, fontSize: 15, lineHeight: 1.4 }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Section>

      <Section labelledBy="fit-title">
        <Eyebrow>A recommendation</Eyebrow>
        <Typography id="fit-title" component="h2" sx={{ ...h2, maxWidth: 720 }}>
          Which format works best?
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            borderTop: `1px solid ${LINE}`,
            borderBottom: { md: `1px solid ${LINE}` },
            mt: 1,
          }}
        >
          {[
            ["30 guests or fewer?", "We usually recommend magic. It's interactive, intimate, and works beautifully in smaller spaces."],
            ["30 guests or more?", "Comedy is a fantastic option. Bring everyone together, put a few great comics on stage, and turn the venue into a comedy club."],
            ["Hosting a cocktail party?", "Strolling magic keeps the energy going without interrupting the party."],
          ].map(([title, copy], index) => (
            <Box
              key={title}
              sx={{
                py: { xs: 3, md: 4 },
                pl: { md: index === 0 ? 0 : 4 },
                pr: { md: index === 2 ? 0 : 4 },
                borderBottom: { xs: `1px solid ${LINE}`, md: "none" },
                borderLeft: { md: index === 0 ? "none" : `1px solid ${LINE}` },
                "&:last-of-type": { borderBottom: "none" },
              }}
            >
              <Typography component="h3" sx={{ color: TEXT, fontSize: { xs: "1.35rem", md: "1.55rem" }, fontWeight: 560, letterSpacing: "-0.03em", lineHeight: 1.15, mb: 1.5, minHeight: { md: "2.3em" } }}>
                {title}
              </Typography>
              <Typography sx={body}>{copy}</Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ ...body, mt: 3, maxWidth: 640 }}>
          These are recommendations, not strict limitations. We can customize the show to fit the venue.
        </Typography>
      </Section>

      <Section labelledBy="production-title">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" }, gap: { xs: 3, md: 6 }, alignItems: "center" }}>
          <Box>
            <Eyebrow>Your space. Our production.</Eyebrow>
            <Typography id="production-title" component="h2" sx={h2}>
              You have the venue. We handle the show.
            </Typography>
            <Typography sx={{ ...body, mb: 3 }}>
              The room stays yours. We mainly handle three things: booking the talent, setting the A/V and production, and running the show.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
              {["Talent booking", "A/V and production setup", "Run of show production"].map((item) => (
                <Typography key={item} component="li" sx={{ color: TEXT, py: 1.1, borderTop: `1px solid ${LINE}`, fontSize: 16 }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
          <Box
            component="img"
            src={PHOTOS.production}
            alt="Gabby Bryan performing at a ComedyUO show"
            loading="lazy"
            sx={{ width: "100%", height: { xs: 240, md: 460 }, objectFit: "cover", objectPosition: "center 35%", display: "block" }}
          />
        </Box>
      </Section>

      <Section labelledBy="proof-title">
        <Eyebrow>Case study</Eyebrow>
        <Typography id="proof-title" component="h2" sx={h2}>
          San Vicente Bungalows.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" }, gap: { xs: 3, md: 6 }, alignItems: "start" }}>
          <Box>
            <Typography sx={{ ...body, mb: 2.5 }}>
              A private members club in the West Village. The room was already theirs. ComedyUO plugged in for the night.
            </Typography>
            <Typography sx={{ ...body, mb: 2.5 }}>
              We booked the talent, set the A/V and production, and ran the show. The club stayed the host.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", mb: 3 }}>
              {[
                "Talent booked for the room",
                "A/V and production set in the space",
                "Run of show handled by ComedyUO",
              ].map((item) => (
                <Typography key={item} component="li" sx={{ color: TEXT, py: 1.1, borderTop: `1px solid ${LINE}`, fontSize: 16 }}>
                  {item}
                </Typography>
              ))}
            </Box>
            <Typography sx={{ color: TEXT, fontSize: { xs: "1.25rem", md: "1.45rem" }, fontWeight: 550, letterSpacing: "-0.02em", lineHeight: 1.35, maxWidth: 520 }}>
              Same job we would do at Kings Athletic Club. Their room. Our show.
            </Typography>
          </Box>
          <Box>
            <Box
              component="img"
              src={PHOTOS.sanVicente}
              alt="San Vicente Bungalows in the West Village"
              loading="lazy"
              sx={{ width: "100%", height: { xs: 280, md: 420 }, objectFit: "cover", display: "block" }}
            />
            <Typography sx={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: ACCENT, mt: 1.5 }}>
              West Village, New York
            </Typography>
          </Box>
        </Box>
      </Section>

      <Section labelledBy="glance-title">
        <Eyebrow>Pricing</Eyebrow>
        <Typography id="glance-title" component="h2" sx={h2}>
          Offerings at a glance.
        </Typography>
        <Box sx={{ display: { xs: "grid", md: "none" }, gap: 1 }}>
          {GLANCE.map(([name, best, format, price]) => (
            <Box key={name} sx={{ background: "#161618", border: `1px solid ${LINE}`, px: 2, py: 1.75 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, mb: 0.75 }}>
                <Typography sx={{ color: TEXT, fontWeight: 650, fontSize: 16, lineHeight: 1.25 }}>{name}</Typography>
                <Typography sx={{ color: TEXT, fontWeight: 650, fontSize: 16, whiteSpace: "nowrap" }}>{price}</Typography>
              </Box>
              <Typography sx={{ color: MUTED, fontSize: 14, lineHeight: 1.4 }}>
                {best}. {format}.
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" }, overflowX: "auto", background: "#161618", border: `1px solid ${LINE}` }}>
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 640,
              "& th": {
                textAlign: "left",
                color: "rgba(244,245,247,0.78)",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                padding: "16px 20px",
                borderBottom: `1px solid ${LINE}`,
              },
              "& td": {
                color: "#ffffff",
                fontSize: 17,
                fontWeight: 500,
                padding: "18px 20px",
                borderBottom: `1px solid ${LINE}`,
              },
              "& tr:last-of-type td": { borderBottom: "none" },
              "& td:first-of-type": { color: "#ffffff", fontWeight: 650 },
            }}
          >
            <thead>
              <tr>
                <th scope="col">Experience</th>
                <th scope="col">Best for</th>
                <th scope="col">Format</th>
                <th scope="col">Price</th>
              </tr>
            </thead>
            <tbody>
              {GLANCE.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell) => (
                    <td key={cell}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </Section>

    </Box>
  );
}

function Section({
  id,
  labelledBy,
  children,
}: {
  id?: string;
  labelledBy: string;
  children: ReactNode;
}) {
  return (
    <Box
      id={id}
      component="section"
      aria-labelledby={labelledBy}
      sx={{
        scrollMarginTop: "24px",
        maxWidth: 1120,
        mx: "auto",
        px: { xs: 2.5, md: 4 },
        py: { xs: 5.5, md: 10 },
      }}
    >
      {children}
    </Box>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: ACCENT, mb: 1.5 }}>
      {children}
    </Typography>
  );
}

const h2 = {
  color: TEXT,
  fontWeight: 560,
  letterSpacing: "-0.03em",
  fontSize: { xs: "1.85rem", sm: "2.15rem", md: "3rem" },
  lineHeight: 1.05,
  mb: 2,
  maxWidth: 760,
};

const body = {
  color: MUTED,
  fontSize: { xs: "1rem", md: "1.05rem" },
  lineHeight: 1.55,
};

const primaryBtn = {
  appearance: "none",
  border: "none",
  cursor: "pointer",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  background: TEXT,
  color: "#111",
  font: "inherit",
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: "0.01em",
  px: 2.25,
  py: 1.35,
  borderRadius: "2px",
  "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 3 },
};

const ghostBtn = {
  ...primaryBtn,
  background: "transparent",
  color: TEXT,
  border: `1px solid rgba(255,255,255,0.55)`,
};
